import React from "react";

const MAX_DEPTH = 12;

function isPrimitive(v: unknown) {
  return v == null || typeof v === "string" || typeof v === "number" || typeof v === "boolean";
}

function getDisplayName(v: any): string {
  if (!v) return "";
  return v.displayName || v?.render?.displayName || v?.name || "";
}

function safePreview(v: any): string {
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

export function assertRenderableChildDeep(
  children: React.ReactNode,
  label: string,
  path: string = label,
  depth: number = 0
) {
  if (depth > MAX_DEPTH) return;

  // React.Children.forEach handles arrays + iterables
  React.Children.forEach(children as any, (child) => {
    if (isPrimitive(child)) return;

    if (React.isValidElement(child)) {
      const typeName =
        typeof child.type === "string"
          ? child.type
          : getDisplayName(child.type) || "AnonymousComponent";

      // Recursively validate nested children
      assertRenderableChildDeep(
        (child.props as any)?.children,
        label,
        `${path} > ${typeName}`,
        depth + 1
      );
      return;
    }

    // If it reaches here, it's an invalid object/function being rendered
    const keys = child && typeof child === "object" ? Object.keys(child) : [];
    const dn = getDisplayName(child);
    throw new Error(
      `[${label}] Invalid React child detected at path="${path}". typeof=${typeof child} displayName="${dn}" keys={${keys.join(",")}} preview=${safePreview(child)}`
    );
  });
}

// Legacy shallow version for backward compatibility
export function assertRenderableChild(children: React.ReactNode, label: string) {
  assertRenderableChildDeep(children, label, label, 0);
}

export function debugEnabled() {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("debug") === "1";
}

